// =============================================================================
// api/analytics.ts
// =============================================================================

import { supabase } from "@cl/supabase";
import { analyticsAllowed } from "@u/consent";
import type { SessionEngagement } from "@u/sessionEngagement";
import type { TrafficTag } from "@u/trafficTag";
import { handleSupabaseError } from "./_error";

// ── Types ─────────────────────────────────────────────────────────────────────

export type DeviceType = "mobile" | "tablet" | "desktop";
export type CtaType = "whatsapp" | "phone" | "email" | "booking";

export type TrafficSource =
  | "qr"
  | "link"
  | "google_maps"
  | "google"
  | "idealista"
  | "fotocasa"
  | "booking"
  | "airbnb"
  | "social"
  | "embed"
  | "direct"
  | "other";

export interface PeriodStats {
  totalSessions: number;
  avgDurationSeconds: number;
}

export interface TourAnalyticsSummary {
  // Current period
  totalSessions: number;
  qualifiedSessions: number;     // duration >= 60s
  bounceRate: number;            // % sessions duration < 15s (of sessions with duration data)
  sceneRetentionRate: number;    // % sessions that viewed >= 2 scenes
  avgDurationSeconds: number;
  totalSceneViews: number;
  avgScenesPerSession: number;
  peakHour: number | null;       // 0–23, hour with most sessions
  weekdayDistribution: { day: number; label: string; count: number }[];
  deviceBreakdown: { mobile: number; tablet: number; desktop: number };
  trafficSources: { source: TrafficSource; count: number }[];
  ctaClicks: { whatsapp: number; phone: number; email: number; booking: number };
  topScenes: { scene_id: string; scene_name: string; views: number }[];
  dailySessions: { date: string; count: number }[];
  // Previous period (for comparison)
  prevPeriod: PeriodStats;
}

// ── Traffic source parser ─────────────────────────────────────────────────────

// Espejo de public.classify_traffic_source (SQL) — mismo orden de reglas.
export function parseTrafficSource(
  referrer: string | null,
  isEmbedded: boolean,
  tag: TrafficTag | null = null,
): TrafficSource {
  if (tag) return tag;
  if (isEmbedded) return "embed";
  if (!referrer) return "direct";
  const url = referrer.toLowerCase();
  if (url.includes("maps.google") || url.includes("google.com/maps"))
    return "google_maps";
  if (url.includes("google")) return "google";
  if (url.includes("idealista")) return "idealista";
  if (url.includes("fotocasa")) return "fotocasa";
  if (url.includes("booking.com")) return "booking";
  if (url.includes("airbnb")) return "airbnb";
  if (
    url.includes("instagram") ||
    url.includes("facebook") ||
    url.includes("linkedin") ||
    url.includes("twitter") ||
    url.includes("tiktok") ||
    url.includes("whatsapp") ||
    url.includes("://t.co/")
  )
    return "social";
  return "other";
}

/**
 * Etiquetas de origen de tráfico. Las marcas son literales (no se traducen);
 * las genéricas (social/embed/direct/other) salen de i18n → pasar `t`.
 */
export function trafficSourceLabels(
  t: (key: string) => string,
): Record<TrafficSource, string> {
  return {
    qr: t("traffic_source_qr"),
    link: t("traffic_source_link"),
    google_maps: "Google Maps",
    google: "Google",
    idealista: "Idealista",
    fotocasa: "Fotocasa",
    booking: "Booking.com",
    airbnb: "Airbnb",
    social: t("traffic_source_social"),
    embed: t("traffic_source_embed"),
    direct: t("traffic_source_direct"),
    other: t("traffic_source_other"),
  };
}

// ── Tracking RPCs (anon safe — SECURITY DEFINER) ──────────────────────────────

export async function trackSessionStart(
  tourId: string,
  deviceType: DeviceType,
  referrer: string,
  isEmbedded: boolean,
  source: TrafficTag | null,
): Promise<string | null> {
  // Opt-out del visitante: sin sesión no se emite ningún evento (trackEvent
  // exige el sessionId que devuelve esta función).
  if (!analyticsAllowed()) return null;
  try {
    const { data, error } = await supabase.rpc("track_session_start", {
      p_tour_id: tourId,
      p_device_type: deviceType,
      p_referrer: referrer.slice(0, 500),
      p_is_embedded: isEmbedded,
      p_source: source,
    });
    if (error) return null;
    return data as string;
  } catch {
    return null;
  }
}

export async function trackSessionEnd(
  sessionId: string,
  durationSeconds: number,
): Promise<void> {
  try {
    await supabase.rpc("track_session_end", {
      p_session_id: sessionId,
      p_duration_seconds: durationSeconds,
    });
  } catch {
    /* fire-and-forget */
  }
}

// ── Feature usage telemetry (owner/dashboard actions, not visitor ROI) ────────

export async function logFeatureEvent(
  feature: string,
  action: string,
  tourId?: string,
  meta?: Record<string, unknown>,
): Promise<void> {
  try {
    await supabase.rpc("log_feature_event", {
      p_feature: feature,
      p_action: action,
      p_tour_id: tourId ?? null,
      p_meta: meta ?? {},
    });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Qué función del tour probó el visitante (tiles de la landing embebedora,
 * click en la brújula). El nombre de la acción viaja en `cta_type` — ver la
 * migration tour_events_remote_action. Fire-and-forget, respeta el opt-out.
 */
export async function trackRemoteAction(
  sessionId: string,
  tourId: string,
  action: string,
  sceneId?: string,
): Promise<void> {
  if (!analyticsAllowed()) return;
  try {
    await supabase.rpc("track_tour_event", {
      p_session_id: sessionId,
      p_tour_id: tourId,
      p_event_type: "remote_action",
      p_scene_id: sceneId ?? null,
      p_hotspot_id: null,
      p_cta_type: action,
    });
  } catch {
    /* fire-and-forget */
  }
}

export async function trackEvent(
  sessionId: string,
  tourId: string,
  eventType:
    | "tour_start"
    | "scene_view"
    | "hotspot_click"
    | "cta_click"
    | "measure_open"
    // Señales de intención de la ficha del inmueble (2026-09-04):
    | "info_open"
    | "faq_open"
    | "directions_click",
  sceneId?: string,
  hotspotId?: string,
  ctaType?: CtaType,
): Promise<void> {
  if (!analyticsAllowed()) return;
  try {
    await supabase.rpc("track_tour_event", {
      p_session_id: sessionId,
      p_tour_id: tourId,
      p_event_type: eventType,
      p_scene_id: sceneId ?? null,
      p_hotspot_id: hotspotId ?? null,
      p_cta_type: ctaType ?? null,
    });
  } catch {
    /* fire-and-forget */
  }
}

// ── Analytics reads (dashboard — owner only) ──────────────────────────────────

export async function fetchTourAnalytics(
  tourId: string,
  days = 30,
): Promise<TourAnalyticsSummary> {
  // HALLAZGO 9: Delegate aggregation to PostgreSQL RPC
  // Returns pre-computed JSON with same structure as TourAnalyticsSummary
  const { data, error } = await supabase.rpc("get_tour_analytics", {
    p_tour_id: tourId,
    p_days: days,
  });

  if (error) handleSupabaseError(error, "fetchTourAnalytics:rpc");

  // La RPC arma cuatro de sus arrays con `jsonb_agg` SIN coalesce, así que un
  // tour sin eventos en la ventana devuelve `null` —no `[]`— en
  // weekdayDistribution / trafficSources / topScenes / dailySessions. El tipo
  // los declara no-nulos y los consumidores hacen `.length`/`.map` directo, así
  // que se normalizan acá, en el único borde por el que pasa la RPC, y no con
  // `?.` repartidos por cada vista.
  const raw = data as TourAnalyticsSummary;
  const result: TourAnalyticsSummary = {
    ...raw,
    weekdayDistribution: raw.weekdayDistribution ?? [],
    trafficSources: raw.trafficSources ?? [],
    topScenes: raw.topScenes ?? [],
    dailySessions: raw.dailySessions ?? [],
  };

  // Resolve weekday labels in user's locale (client-side, browser-aware)
  if (result.weekdayDistribution.length) {
    result.weekdayDistribution = result.weekdayDistribution.map((wd) => ({
      day: wd.day,
      label: new Intl.DateTimeFormat("default", { weekday: "short" }).format(
        new Date(2023, 0, wd.day + 1), // Jan 1 2023 = Sunday (matches RPC baseline)
      ),
      count: wd.count,
    }));
  }

  return result;
}

/**
 * Session count per tour over a window — the traffic side of the analytics
 * "traffic but no leads" insight. Same read as the overview roll-up, but keyed
 * by tour instead of collapsed to a single top tour.
 */
export async function fetchSessionsByTour(
  tourIds: string[],
  days = 30,
): Promise<Record<string, number>> {
  if (tourIds.length === 0) return {};
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { data, error } = await supabase
    .from("tour_sessions")
    .select("tour_id")
    .gte("started_at", since)
    .in("tour_id", tourIds);

  if (error) handleSupabaseError(error, "fetchSessionsByTour");

  const out: Record<string, number> = {};
  for (const row of data ?? []) out[row.tour_id] = (out[row.tour_id] ?? 0) + 1;
  return out;
}

/**
 * Per-session engagement for a set of session ids — the "did this booker/lead
 * actually tour, and how much" read. Batches the whole visible list into one
 * query over v_session_engagement (owner-RLS inherited). Returns a Map keyed by
 * session_id; sessions not found (manual/portfolio/pre-ship) are simply absent.
 */
export async function fetchSessionEngagement(
  sessionIds: string[],
): Promise<Map<string, SessionEngagement>> {
  const ids = [...new Set(sessionIds.filter(Boolean))];
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase
    .from("v_session_engagement")
    .select(
      "session_id, duration_seconds, distinct_scenes, faq_opens, directions_clicks",
    )
    .in("session_id", ids);

  if (error) handleSupabaseError(error, "fetchSessionEngagement");

  const out = new Map<string, SessionEngagement>();
  for (const row of (data ?? []) as SessionEngagement[]) {
    out.set(row.session_id, row);
  }
  return out;
}

// ── Monthly report ────────────────────────────────────────────────────────────

export async function sendAnalyticsReport(
  tourId: string,
  to?: string,
): Promise<void> {
  const { error } = await supabase.functions.invoke("send-analytics-report", {
    body: { tour_id: tourId, to: to?.trim() || undefined },
  });
  if (error) throw error;
}

// ── Public platform stats (landing social proof) ──────────────────────────────

/** Aggregate-only totals across the platform. Counts, never rows — see the
 *  `public_platform_stats` RPC. Demo/template resources are excluded server-side. */
export interface PlatformStats {
  tours: number;
  tour_minutes: number;
  tour_avg_seconds: number;
  tour_sessions: number;
  venues: number;
  scans: number;
}

export async function fetchPlatformStats(): Promise<PlatformStats> {
  const { data, error } = await supabase.rpc("public_platform_stats");
  if (error) handleSupabaseError(error, "fetchPlatformStats");
  return data as PlatformStats;
}
